// @ts-check

const ZOOM_MEDIA = '.custom-media-zoom';
const ZOOM_DURATION = 1200;
const ZOOM_EASING = 'cubic-bezier(0.5, 0, 0, 1)';

/** @type {WeakSet<Element>} */
const zoomMediaSeen = new WeakSet();

/**
 * @param {Element} media
 * @param {number} duration
 */
function settleZoom(media, duration) {
  media.animate({ scale: 'none' }, { duration, easing: ZOOM_EASING, fill: 'forwards' });
}

/** @param {Element[]} stack */
async function playZoom(stack) {
  const images = stack.flatMap((layer) =>
    layer instanceof HTMLImageElement ? [layer] : [...layer.querySelectorAll('img')]
  );
  const shown = images.filter((image) => image.getClientRects().length > 0);
  await Promise.all(shown.map((image) => image.decode().catch(() => {})));
  for (const layer of stack) settleZoom(layer, ZOOM_DURATION);
}

const zoomObserver = new IntersectionObserver(
  (entries) => {
    /** @type {Map<string, Element[]>} */
    const stacks = new Map();
    for (const { isIntersecting, target, boundingClientRect: box } of entries) {
      if (!isIntersecting) continue;
      zoomObserver.unobserve(target);
      const key = [box.x, box.y, box.width, box.height].join();
      stacks.set(key, [...(stacks.get(key) ?? []), target]);
    }
    for (const stack of stacks.values()) void playZoom(stack);
  },
  { rootMargin: '0px 0px -15% 0px' }
);

for (const media of document.querySelectorAll(ZOOM_MEDIA)) {
  zoomMediaSeen.add(media);
  zoomObserver.observe(media);
}

new MutationObserver((records) => {
  for (const { addedNodes } of records) {
    for (const node of addedNodes) {
      if (!(node instanceof Element)) continue;
      for (const media of [node, ...node.querySelectorAll(ZOOM_MEDIA)]) {
        if (zoomMediaSeen.has(media) || !media.matches(ZOOM_MEDIA)) continue;
        zoomMediaSeen.add(media);
        settleZoom(media, 0);
      }
    }
  }
}).observe(document.body, { childList: true, subtree: true });
